import React, { useEffect, useState } from 'react';
import RenderFieldBasedOnType from '../RenderFieldsBasedOnType';
import {
    Box,
    Grid,
    Typography,
    Tooltip,
    IconButton,
} from '@material-ui/core'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import { Helpers } from "dsilo-ui-components";
import { makeStyles } from '@material-ui/core/styles';
import SubDataModelTaskViewer from "../SubDataModelTask"
import CommonTableViewer from './CommonTableViewer'
import InfoHelpModal from './InfoHelpModal'
// import { padding } from 'polished'; // (optional) not used

/* NEW: responsive card styles */
import { useCardStyles, useCommonStyles, useInputStyles } from './style';

/* For now It is static */
const defaultOptions = {}

/**
 * Optional field-level help modal (info icon when `isModelTrue` + `modalConfig`).
 *
 * **Table example**
 * ```json
 * {
 *   "value": "supplier",
 *   "label": "Supplier",
 *   "isRequired": true,
 *   "isModelTrue": true,
 *   "tooltipText": "Supplier name",
 *   "xs": 3,
 *   "modalConfig": {
 *     "type": "table",
 *     "modalWidth": "800px",
 *     "modalHeight": "auto",
 *     "title": "Revenue leakage reference",
 *     "columns": ["Choice", "Description", "Revenue Leakage Opportunity?"],
 *     "rowKeys": ["choice", "description", "opportunity"],
 *     "rows": [
 *       {
 *         "choice": "Price Discrepancy",
 *         "description": ["Applied wrong price to the part", "Applied wrong escalation %", "Price escalation not applied"],
 *         "opportunity": "Yes"
 *       }
 *     ]
 *   }
 * }
 * ```
 *
 * **Text example**
 * ```json
 * "modalConfig": {
 *   "type": "text",
 *   "modalWidth": "480px",
 *   "modalHeight": "auto",
 *   "title": "Help",
 *   "body": "Plain text. Line breaks are preserved."
 * }
 * ```
 */

/** NEW (no package): normalize newlines + very basic sanitization */
const normalizeToSafeHtml = (value) => {
    if (value === null || value === undefined) return '';

    let html = String(value);

    // Handle both literal "\n" text and real newlines
    html = html
        .replace(/\\n/g, '\n')   // turns "\\n" into newline
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');

    // Convert newlines to <br/> so HTML renders line breaks
    html = html.replace(/\n/g, '<br/>');

    /**
     * Very basic safety (not perfect, but better than raw):
     * - remove script/style tags
     * - remove inline event handlers like onclick="..."
     * - remove javascript: URLs
     */
    html = html
        .replace(/<\s*script[^>]*>[\s\S]*?<\s*\/\s*script\s*>/gi, '')
        .replace(/<\s*style[^>]*>[\s\S]*?<\s*\/\s*style\s*>/gi, '')
        .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, '') // onClick="..." etc
        .replace(/javascript:/gi, '');

    return html;
};

const SummaryBlock = (props) => {
    const layoutClasses = useCommonStyles()
    const inputClasses = useInputStyles();
    const cardClasses = useCardStyles(); // NEW

    const { currentDocumentId, formData, selectedStep, block, blockState, handleCheckboxChange, onChange } = props
    const { chartUniqueId } = props.data

    const [blockHeaders, setBlockHeaders] = useState([])
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [allTasksCompleted, setAllTasksCompleted] = useState(false);
    const [infoModalElement, setInfoModalElement] = useState(null);
    const shouldDisableFields = props.shouldDisableFields


    const handleInfoModalClose = (_, reason) => {
        if (reason === 'backdropClick') {
            setInfoModalOpen(false);
        }
    }

    const handleInfoModalExited = () => {
        setInfoModalElement(null);
    }

    const getCardValue = (blockState, block, dataElementKey, defaultText) => {
        return blockState?.[dataElementKey] ||
            blockState?.[dataElementKey] ||
            blockState?.[dataElementKey] ||
            defaultText;
    };

    /**
     * Finds a field on a linked-tab row. Linked rows group data elements under section
     * names (e.g. obligationManagement), so `parentField` points at that container;
     * without it, the row is searched at the top level and then one level deep.
     */
    const getLinkedRowField = (item, key, parentField) => {
        if (!item || !key) return undefined;

        if (parentField) {
            const parent = item?.[parentField];
            if (parent && typeof parent === 'object') return parent?.[key];
            return undefined;
        }

        if (item?.[key] !== undefined) return item[key];

        const nestedMatch = Object.values(item).find(
            (section) => section && typeof section === 'object' && !Array.isArray(section) && section[key] !== undefined,
        );
        return nestedMatch?.[key];
    };

    /**
     * Resolves a title template like "Claim Obligation - ${originalName}".
     * When item is missing (no linked contract), strips placeholder segments → "Claim Obligation".
     */
    const resolveLinkedTitle = (titleTemplate, item, parentField) => {
        if (!titleTemplate) return '';
        if (!item) {
            return String(titleTemplate)
                .replace(/\s*-\s*\$\{[^}]+\}/g, '')
                .replace(/\$\{[^}]+\}/g, '')
                .trim();
        }
        return String(titleTemplate)
            .replace(/\$\{([^}]+)\}/g, (_, key) => {
                const raw = item?.[key] !== undefined ? item[key] : getLinkedRowField(item, key, parentField);
                if (raw == null || raw === '') return '';
                if (typeof raw === 'object') {
                    return String(raw?.value ?? raw?.name ?? raw?.label ?? '');
                }
                return String(raw);
            })
            .replace(/\s*-\s*$/g, '')
            .replace(/\s{2,}/g, ' ')
            .trim();
    };

    /**
     * Resolves linkedFields card body from a linked-tab row.
     * - no outputField → claimObligation.value
     * - outputField: "value" → claimObligation.value
     * - outputField: "sentence" → claimObligation.sentence[0].value
     */
    const getLinkedFieldValue = (item, config) => {
        const key = config?.value;
        if (!key || !item) return config?.defaultText || '';
        const raw = getLinkedRowField(item, key, config?.parentField);
        if (raw == null || raw === '') return config?.defaultText || '';

        // Primitive stored directly under the field key
        if (typeof raw !== 'object') return raw;

        const outputField = config?.outputField || 'value';
        const out = raw?.[outputField];

        if (Array.isArray(out)) {
            return out?.[0]?.value ?? config?.defaultText ?? '';
        }
        if (out != null && out !== '') {
            return typeof out === 'object' ? (out?.value ?? config?.defaultText ?? '') : out;
        }
        return config?.defaultText || '';
    };

    /** Rows for linkedFields come from the tab named in linkedFrom (e.g. contractDetails → linked contracts). */
    const getLinkedSourceRows = (linkedFrom) => {
        if (!linkedFrom) return [];
        if (linkedFrom === 'contractDetails') {
            return Array.isArray(props.tableData) ? props.tableData : [];
        }
        if (linkedFrom === 'spendDetails') {
            const spend = props.formData?.document?.[`${chartUniqueId}_spendDetails_tableData`];
            if (Array.isArray(spend?.data)) return spend.data;
            if (Array.isArray(spend)) return spend;
            return [];
        }
        const tabData = props.formData?.document?.[`${chartUniqueId}_${linkedFrom}_tableData`];
        if (Array.isArray(tabData?.data)) return tabData.data;
        if (Array.isArray(tabData)) return tabData;
        return [];
    };

    /** Renders one or more cards from another tab's data (linkedFields custom type). */
    const renderLinkedFieldsCards = (config) => {
        const rows = getLinkedSourceRows(config?.linkedFrom);
        if (!rows.length) {
            return (
                <div className={cardClasses.cardsColumn}>
                    {renderCard(
                        { ...config, title: resolveLinkedTitle(config?.title, null, config?.parentField), isHTML: true },
                        config?.defaultText || '',
                        0,
                    )}
                </div>
            );
        }
        return (
            <div className={cardClasses.cardsColumn}>
                {rows.map((item, index) => {
                    const cardValue = getLinkedFieldValue(item, config);
                    const title = resolveLinkedTitle(config?.title, item, config?.parentField);
                    return renderCard(
                        { ...config, title, isHTML: true },
                        cardValue || config?.defaultText || '',
                        index,
                    );
                })}
            </div>
        );
    };

    console.log('block**props', props.enableWorkflow)

    /* CHANGED: responsive card rendering */
    const renderCard = (card, cardValue, index) => {
        const hasData = cardValue && cardValue !== card.defaultText && String(cardValue).trim() !== '';

        // NEW: handle HTML + \n / \\n
        const isHtml = !!card?.isHTML;              // expects isHTML passed into card
        const safeHtml = normalizeToSafeHtml(cardValue);

        return (
            <div
                key={`${card.value}_${index}`}
                className={`${cardClasses.cardRoot} ${hasData ? cardClasses.cardRootHasData : ''}`}
                style={{
                    // pass colors in as CSS vars the JSS uses
                    '--card-bg': card.backgroundColor,
                    '--card-border': card.borderColor,
                }}
            >

                <div className={cardClasses.cardIconWrap}>
                    <img
                        src={card.icon}
                        alt={card.title}
                        width={card.title === "Obligation Language" ? 18 : 23}
                        height={card.title === "Obligation Language" ? 18 : 23}
                    />
                </div>

                <div className={cardClasses.cardBody}>
                    <Typography
                        className={cardClasses.cardTitle}
                        style={{ color: card.titleColor }}
                    >
                        {card.title}
                    </Typography>

                    {/* CHANGED: render HTML when isHTML=true, else render plain with pre-line */}
                    <Typography className={cardClasses.cardText} component="div">
                        {isHtml ? (
                            <span dangerouslySetInnerHTML={{ __html: safeHtml }} />
                        ) : (
                            <span style={{ whiteSpace: 'pre-line' }}>
                                {String(cardValue || '').replace(/\\n/g, '\n')}
                            </span>
                        )}
                    </Typography>
                </div>
            </div>
        );
    };

    // CHANGED: accept isHTML and pass it into renderCard
    const renderDynamicCards = (blockState, block, isHTML) => {
        const cardValue = getCardValue(blockState, block, block.value, block.defaultText);
        return (
            <div className={cardClasses.cardsColumn}>
                {renderCard({ ...block, isHTML }, cardValue, 0)}
            </div>
        );
    };

    useEffect(() => {
        if (formData?.dataModel[selectedStep.dataModel]) {
            let dataElements = formData.dataModel[selectedStep.dataModel].dataElements || []
            if (dataElements?.length) {
                let fdes = []
                if (block?.config?.dataElements) {
                    fdes = JSON.parse(JSON.stringify(block?.config?.dataElements))
                    fdes = fdes.map(fde => {
                        let sde = dataElements.find(de => de.name === fde.value)
                        if (sde?.name) {
                            return { ...sde, ...fde }
                        } else {
                            return fde
                        }
                    })
                    setBlockHeaders(fdes)
                }
            }
        }
    }, [formData?.dataModel, selectedStep?.dataModel, block?.config?.dataElements])

    const showHideWorkFlow = (obj) => {
        setAllTasksCompleted(obj)
        props.showHideWorkFlow(obj)
    }

    return (
        <React.Fragment>
            <div className={layoutClasses.columnContainer}>
                {
                    props.isSubBlock ? (<Tooltip title={block?.name || ''} arrow>
                        <Typography
                            component='span'
                            noWrap
                            className={`${layoutClasses.sectionTitleText} ${layoutClasses.sectionTitleTextClamp}`}
                        >
                            {block.name} :
                        </Typography>
                    </Tooltip>) : null
                }
                <Grid container spacing={1}>
                    {
                        blockHeaders?.length > 0 ? blockHeaders.map((element, index) => {
                            let isFieldReadOnly = shouldDisableFields || element.readOnly;

                            if (!props.enableWorkflow) {
                                const result = !element?.roleEnabled?.some(enabledRole =>
                                    props?.members?.roles?.some(role => role?.name === enabledRole?.name) &&
                                    enabledRole?.opertions?.includes("EDIT")
                                );

                                if (element?.roleEnabled) {
                                    isFieldReadOnly = result;
                                }
                            }

                            if ((!props.enableWorkflow) && (element?.isCustomType?.enable === true && element?.isCustomType?.type === "linkedReferenceTasks")) {
                                return null;
                            }
                            return (
                                <Grid
                                    item
                                    key={index}
                                    xs={element.xs ? element.xs : (12 / blockHeaders.length)}
                                    className={layoutClasses.gridItemMargins}
                                >
                                    {
                                        element?.isCustomType?.enable && element?.isCustomType?.type === "linkedReferenceTasks" ? (
                                            props.enableWorkflow ? (<SubDataModelTaskViewer {...props} element={element} eachBlock={element?.isCustomType} block={element?.isCustomType} keyName={element?.isCustomType?.dataModel} currentOpportunityId={currentDocumentId} shouldDisableFields={shouldDisableFields} showHideWorkFlow={showHideWorkFlow} />) : null
                                        ) : element?.isCustomType?.enable && element?.isCustomType?.type === "linkedFields" ? (
                                            renderLinkedFieldsCards(element?.isCustomType)
                                        ) : element?.isCustomType?.enable && element?.isCustomType?.type === "card" ? (
                                            // CHANGED: pass element.isHTML into renderDynamicCards
                                            renderDynamicCards(blockState, element?.isCustomType, true)
                                        ) :
                                            element?.isCustomType?.enable && element?.isCustomType?.type === "table" && element?.isCustomType?.isInlineObject ? (
                                                <CommonTableViewer {...props} title={element?.isCustomType?.title} data={blockState?.[element?.isCustomType?.value] || []} element={element?.isCustomType} />
                                            ) :
                                                (
                                                    <Box className={layoutClasses.fullWidthBox}>
                                                        <Grid container>
                                                            <div className={layoutClasses.labelRow}>
                                                                {/* <Tooltip title={element?.label || ''} arrow>
                                                                    <Typography
                                                                        component='span'
                                                                        className={`${layoutClasses.fieldLabel} ${layoutClasses.fieldLabelClamp}`}
                                                                    >
                                                                        {element.label} : //here i want a info icon when user click on that then a model need to open 
                                                                    </Typography>
                                                                </Tooltip> */}
                                                                <div className={layoutClasses.labelWithInfo}>
                                                                    <Tooltip title={element?.tooltipText || element?.label || ''} arrow>
                                                                        <Typography
                                                                            component='span'
                                                                            className={`${layoutClasses.fieldLabel} ${layoutClasses.fieldLabelClamp}`}
                                                                        >
                                                                            {element.label} :
                                                                        </Typography>
                                                                    </Tooltip>
                                                                    {element?.isModelTrue && element?.modalConfig ? (
                                                                        <IconButton
                                                                            size="small"
                                                                            className={layoutClasses.infoIconButton}
                                                                            aria-label={`More information: ${element.label}`}
                                                                            onClick={() => {
                                                                                setInfoModalElement(element);
                                                                                setInfoModalOpen(true);
                                                                            }}
                                                                        >
                                                                            <InfoOutlined />
                                                                        </IconButton>
                                                                    ) : null}
                                                                </div>
                                                            </div>

                                                            <div className={layoutClasses.fieldWrapper}>
                                                                <RenderFieldBasedOnType
                                                                    {...props}
                                                                    classes={layoutClasses}
                                                                    element={isFieldReadOnly ? { ...element, readOnly: true } : element}
                                                                    onChange={onChange}
                                                                    handleCheckboxChange={handleCheckboxChange}
                                                                    dataObj={blockState}
                                                                    options={defaultOptions[element.value || element.name]}
                                                                    selectedStep={selectedStep}
                                                                    inputClasses={inputClasses}
                                                                />
                                                            </div>
                                                        </Grid>
                                                    </Box>
                                                )
                                    }
                                </Grid>
                            )
                        }) : (
                            <Typography>{`No Fields are Configured`}</Typography>
                        )
                    }
                </Grid>
            </div>

            <InfoHelpModal
                open={infoModalOpen}
                onClose={handleInfoModalClose}
                onExited={handleInfoModalExited}
                modalConfig={infoModalElement?.modalConfig}
                label={infoModalElement?.label}
            />
        </React.Fragment>
    )
}

export default SummaryBlock